<template>
  <ContentSection :spacing="index === 0 ? 'inline_text_top' : ''">
    <Container indent="wide">
      <div
        class="xl:mb-x-12 mb-10 flex flex-wrap items-end gap-4 sm:gap-6 2xl:justify-between"
      >
        <!-- FILTER -->
        <div>
          <Filter
            v-if="Array.isArray(terms) && terms.length > 0"
            :terms="terms"
            :filtered="filter.articleType"
            :button-all-text="$t('all')"
            @filter="filter.articleType = $event"
          >
            {{ $t('filter.filter_for_type') }}
          </Filter>
        </div>
        <!-- TEXT SEARCH -->
        <SearchInput
          class="hidden w-full min-w-52 sm:block lg:max-w-[20rem]"
          :model-value="filter.text"
          @update:model-value="filter.text = $event"
        />
        <!-- TEXT SEARCH MOBILE -->
        <div
          class="relative min-h-[1.625rem] w-full min-w-52 sm:hidden lg:max-w-[20rem]"
        >
          <TransitionExpand
            class="w-full overflow-hidden transition-all delay-200 duration-300 ease-in-out"
            :open="isShowingMore"
          >
            <SearchInput
              class="w-full min-w-52 lg:max-w-[20rem]"
              small
              :model-value="filter.text"
              @update:model-value="filter.text = $event"
            />
          </TransitionExpand>
          <transition name="fade">
            <button
              v-show="!isShowingMore"
              class="absolute left-0 top-0 flex items-center gap-2 text-base text-blue-800"
              @click="isShowingMore = true"
            >
              <PlusIcon class="w-2.5 shrink-0" />
              <span>{{ $t('show_more') }}</span>
            </button>
          </transition>
        </div>
      </div>
    </Container>

    <!-- SUSTAINABILITY ARTICLES -->
    <Container>
      <ul class="grid w-full grid-cols-2 gap-4 sm:gap-6 xl:grid-cols-4">
        <li
          v-for="article in articlesComposed"
          :key="article.id"
          :class="article.colSpan"
        >
          <SustainabilityCard :data="article" />
        </li>
      </ul>

      <!-- LOAD MORE -->
      <div
        v-show="hasMorePages || articles.length > articlesComposed.length"
        class="mt-14 flex w-full justify-center"
      >
        <AppButton @click="filter.limit += initialLimit">
          {{ $t('filter.load_more') }}
        </AppButton>
      </div>

      <!-- NO ENTRIES FOUND -->
      <transition name="fade">
        <div
          v-show="articlesComposed.length === 0"
          class="my-10 flex w-full items-center justify-center gap-4 text-blue-dark-800 sm:my-20"
        >
          <MagnifyingLensIcon class="w-[1.125rem] shrink-0" />
          <p class="text-md text-blue-dark-800">
            {{ $t('filter.no_entries_found') }}
          </p>
        </div>
      </transition>
    </Container>
  </ContentSection>
</template>

<script setup>
const props = defineProps({
  data: { type: Object, default: null },
  index: { type: Number, default: null },
})

const { locale } = useI18n()
const route = useRoute()
const params = useUrlSearchParams('history')
const articles = ref(null)
const terms = ref(null)
const hasMorePages = ref(null)
const initialLimit = ref(16)
const isShowingMore = ref(false)

provide(
  'articles',
  computed(() => articlesComposed.value)
)

// Populate the filter
let routeQueryFilter = route.query[`nachhaltigkeitshub-${props.data.id}`]
if (routeQueryFilter) {
  routeQueryFilter = JSON.parse(routeQueryFilter)
}

// Could be that the route has ha filter query when routed from a news pages TypeTag
const initialFilterArticleType = route.query.articleType || null

const filter = ref(
  routeQueryFilter || {
    articleType: initialFilterArticleType,
    text: null,
    limit: initialLimit.value,
  }
)

// WATCHERS
watch(
  filter,
  (newValue) => {
    params[`nachhaltigkeitshub-${props.data.id}`] = JSON.stringify(newValue)
    fetchArticles()
  },
  { deep: true }
)

// COMPUTED
const articlesComposed = computed(() => {
  if (!Array.isArray(articles.value) || !articles.value.length) {
    return []
  }
  const articlesAmount = articles.value.length
  let articlesComposed = []

  // Loop through the news and determine the colSpan
  for (let index = 0; index < articlesAmount; index++) {
    const article = JSON.parse(JSON.stringify(articles.value[index]))
    const articleBefore = index > 0 ? articles.value[index - 1] : null
    const articleAfter =
      index < articlesAmount - 1 ? articles.value[index + 1] : null
    const isVideo = !!article.image_or_video[0]?.video
    const isImage = !isVideo

    // If the article is a video = col-span-2
    if (isVideo) {
      article.colSpan = 'col-span-2'

      if (index % 3 === 0 && articleBefore?.image_or_video[0]?.image) {
        articlesComposed[articlesComposed.length - 1].colSpan = 'col-span-2'
      }

      articlesComposed.push(article)
      continue
    }

    if (isImage) {
      if (
        articleBefore?.image_or_video[0]?.video &&
        articleAfter?.image_or_video[0]?.video
      ) {
        article.colSpan = 'col-span-2'
      } else if (index % 2 === 0 && articleAfter?.image_or_video[0]?.video) {
        article.colSpan = 'col-span-2 xl:col-span-1'
      } else if (!articleAfter && index % 3 === 0) {
        article.colSpan = 'col-span-2'
      } else {
        article.colSpan = 'col-span-2 md:col-span-1'
      }
      articlesComposed.push(article)
      continue
    }
  }

  return articlesComposed
})

// FUNCTIONS
async function fetchArticles() {
  const operation =
    props.data?.collection?.value === 'sustainability_facts'
      ? 'SustainabilityFacts'
      : 'SustainabilityArticles'
  let payload = await useAsyncGql({
    operation,
    variables: {
      sort: 'date desc',
      limit: filter.value.limit,
      filter: {
        site: locale.value,
        article_type: { contains: filter.value.articleType },
        title: { contains: filter.value.text },
      },
    },
  })

  payload = payload.data.value?.entries

  hasMorePages.value = payload.has_more_pages

  if (!payload) {
    console.error('Articles could not be fetched')
    return
  }

  articles.value = payload.data
}

// INITIAL
const operation =
  props.data?.collection?.value === 'sustainability_facts'
    ? 'SustainabilityFactsAndTerms'
    : 'SustainabilityArticlesAndTerms'
let payload = await useAsyncGql({
  operation,
  variables: {
    sort: 'date desc',
    limit: filter.value.limit,
    filter: {
      site: locale.value,
      article_type: { contains: filter.value.articleType },
      title: { contains: filter.value.text },
    },
    filterLocale: {
      site: locale.value,
    },
  },
})

payload = payload.data?.value

if (!payload) {
  console.error('News could not be fetched')
}

terms.value = payload.article_types?.data

articles.value = payload.entries.data

hasMorePages.value = payload.entries.has_more_pages
</script>
